export type AqueductCameraState = {
	yaw: number;
	pitch: number;
	radius: number;
	target?: [number, number, number];
};

export type AqueductCameraControlsOptions = {
	yaw: number;
	pitch: number;
	radius: number;
	target?: [number, number, number];
	rotateSpeed?: number;
	enablePitch?: boolean;
	enableZoom?: boolean;
	enableMomentum?: boolean;
	momentumDecay?: number;
	minPitch?: number;
	maxPitch?: number;
	minRadius?: number;
	maxRadius?: number;
	wheelZoomSpeed?: number;
};

type DragSample = {
	time: number;
	yaw: number;
	pitch: number;
};

const DEFAULT_ROTATE_SPEED = 1.0;
const DEFAULT_MOMENTUM_DECAY = 7.5;
const DEFAULT_MIN_PITCH = -1.35;
const DEFAULT_MAX_PITCH = 1.35;
const DEFAULT_MIN_RADIUS = 0.15;
const DEFAULT_MAX_RADIUS = 10.0;
const DEFAULT_WHEEL_ZOOM_SPEED = 0.001;
// macOS trackpad pinch arrives as ctrl+wheel with tiny deltas. ~10x the wheel speed puts it
// at ~exp(deltaY*0.01), the canonical native pinch-zoom mapping. Tune here if it feels off.
const PINCH_ZOOM_GAIN = 10;
const WHEEL_LINE_HEIGHT_PX = 16; // deltaMode=1 (lines) -> px
const WHEEL_PAGE_HEIGHT_PX = 100; // deltaMode=2 (pages) -> px (approx)
const MIN_MOMENTUM_VELOCITY = 0.01;
const MAX_MOMENTUM_DT = 0.05;
const MOMENTUM_SAMPLE_WINDOW_MS = 90;
const MOMENTUM_RELEASE_GRACE_MS = 75;
const PITCH_SOFT_LIMIT_ZONE = 0.28;
const PITCH_SOFT_LIMIT_EXTRA_DECAY = 5.0;

function clamp(value: number, min: number, max: number): number {
	return Math.max(min, Math.min(max, value));
}

function smoothstep01(value: number): number {
	const t = clamp(value, 0, 1);
	return t * t * (3 - 2 * t);
}

export class AqueductCameraControls {
	private yaw: number;
	private pitch: number;
	private radius: number;
	private target: [number, number, number] = [0, 0, 0];
	private rotateSpeed: number;
	private enablePitch: boolean;
	private enableZoom: boolean;
	private enableMomentum: boolean;
	private momentumDecay: number;
	private minPitch: number;
	private maxPitch: number;
	private minRadius: number;
	private maxRadius: number;
	private wheelZoomSpeed: number;
	private dragging = false;
	private pinching = false;
	private lastX = 0;
	private lastY = 0;
	private pinchLastDistance = 0;
	private yawVelocity = 0;
	private pitchVelocity = 0;
	private lastMomentumTime = 0;
	private dragSamples: DragSample[] = [];

	constructor(options: AqueductCameraControlsOptions) {
		this.yaw = options.yaw;
		this.pitch = options.pitch;
		this.radius = options.radius;
		this.rotateSpeed = options.rotateSpeed ?? DEFAULT_ROTATE_SPEED;
		this.enablePitch = options.enablePitch ?? true;
		this.enableZoom = options.enableZoom ?? true;
		this.enableMomentum = options.enableMomentum ?? false;
		this.momentumDecay = options.momentumDecay ?? DEFAULT_MOMENTUM_DECAY;
		this.minPitch = options.minPitch ?? DEFAULT_MIN_PITCH;
		this.maxPitch = options.maxPitch ?? DEFAULT_MAX_PITCH;
		this.minRadius = options.minRadius ?? DEFAULT_MIN_RADIUS;
		this.maxRadius = options.maxRadius ?? DEFAULT_MAX_RADIUS;
		this.wheelZoomSpeed = options.wheelZoomSpeed ?? DEFAULT_WHEEL_ZOOM_SPEED;
		this.setState({ target: options.target });
	}

	getState(): AqueductCameraState {
		return { yaw: this.yaw, pitch: this.pitch, radius: this.radius, target: [...this.target] };
	}

	setState(state: Partial<AqueductCameraState>): void {
		if (state.target !== undefined) {
			this.target =
				Array.isArray(state.target) &&
				state.target.length === 3 &&
				state.target.every(Number.isFinite)
					? [...state.target]
					: [0, 0, 0];
		}
		if (state.yaw !== undefined) this.yaw = state.yaw;
		if (state.pitch !== undefined) this.pitch = clamp(state.pitch, this.minPitch, this.maxPitch);
		if (state.radius !== undefined) {
			this.radius = clamp(state.radius, this.minRadius, this.maxRadius);
		}
	}

	configure(
		options: Partial<Omit<AqueductCameraControlsOptions, keyof AqueductCameraState>>
	): void {
		if (options.rotateSpeed !== undefined) this.rotateSpeed = options.rotateSpeed;
		if (options.enablePitch !== undefined) this.enablePitch = options.enablePitch;
		if (options.enableZoom !== undefined) this.enableZoom = options.enableZoom;
		if (options.enableMomentum !== undefined) this.enableMomentum = options.enableMomentum;
		if (options.momentumDecay !== undefined) this.momentumDecay = options.momentumDecay;
		if (options.minPitch !== undefined) this.minPitch = options.minPitch;
		if (options.maxPitch !== undefined) this.maxPitch = options.maxPitch;
		if (options.minRadius !== undefined) this.minRadius = options.minRadius;
		if (options.maxRadius !== undefined) this.maxRadius = options.maxRadius;
		if (options.wheelZoomSpeed !== undefined) this.wheelZoomSpeed = options.wheelZoomSpeed;
	}

	beginPointerDrag(clientX: number, clientY: number, now: number): boolean {
		if (this.pinching) return false;
		this.cancelMomentum();
		this.dragging = true;
		this.lastX = clientX;
		this.lastY = clientY;
		this.dragSamples = [{ time: now, yaw: this.yaw, pitch: this.pitch }];
		return true;
	}

	movePointerDrag(clientX: number, clientY: number, viewportHeight: number, now: number): boolean {
		if (!this.dragging || this.pinching) return false;
		const dx = clientX - this.lastX;
		const dy = clientY - this.lastY;
		const rotateScale = (2 * Math.PI * this.rotateSpeed) / Math.max(1, viewportHeight);

		this.lastX = clientX;
		this.lastY = clientY;
		this.yaw -= dx * rotateScale;
		if (this.enablePitch) {
			this.pitch = clamp(this.pitch + dy * rotateScale, this.minPitch, this.maxPitch);
		}

		if (this.enableMomentum) {
			this.dragSamples = [
				...this.dragSamples.filter((sample) => now - sample.time <= MOMENTUM_SAMPLE_WINDOW_MS),
				{ time: now, yaw: this.yaw, pitch: this.pitch }
			];
		}
		return true;
	}

	endPointerDrag(now: number): boolean {
		this.dragging = false;
		if (!this.enableMomentum) return false;

		const recentSamples = this.dragSamples.filter(
			(sample) => now - sample.time <= MOMENTUM_SAMPLE_WINDOW_MS
		);
		const latestSample = recentSamples.at(-1);
		if (
			recentSamples.length < 2 ||
			!latestSample ||
			now - latestSample.time > MOMENTUM_RELEASE_GRACE_MS
		) {
			this.cancelMomentum();
			this.dragSamples = [];
			return false;
		}

		const firstSample = recentSamples[0];
		const sampleDuration = Math.max(0.001, (latestSample.time - firstSample.time) / 1000);
		this.yawVelocity = (latestSample.yaw - firstSample.yaw) / sampleDuration;
		this.pitchVelocity = this.enablePitch
			? (latestSample.pitch - firstSample.pitch) / sampleDuration
			: 0;
		this.dragSamples = [];

		if (
			Math.abs(this.yawVelocity) <= MIN_MOMENTUM_VELOCITY &&
			Math.abs(this.pitchVelocity) <= MIN_MOMENTUM_VELOCITY
		) {
			this.cancelMomentum();
			return false;
		}
		this.lastMomentumTime = now;
		return true;
	}

	advanceMomentum(now: number): { changed: boolean; active: boolean } {
		const yawActive = Math.abs(this.yawVelocity) > MIN_MOMENTUM_VELOCITY;
		const pitchActive = this.enablePitch && Math.abs(this.pitchVelocity) > MIN_MOMENTUM_VELOCITY;
		if (!yawActive && !pitchActive) {
			this.cancelMomentum();
			return { changed: false, active: false };
		}

		const dt = Math.min(MAX_MOMENTUM_DT, Math.max(0.001, (now - this.lastMomentumTime) / 1000));
		this.lastMomentumTime = now;
		if (yawActive) {
			this.yaw += this.yawVelocity * dt;
			this.yawVelocity *= Math.exp(-this.momentumDecay * dt);
		}
		if (pitchActive) {
			const pitchScale = this.getPitchMomentumScale(this.pitchVelocity);
			const nextPitch = clamp(
				this.pitch + this.pitchVelocity * pitchScale * dt,
				this.minPitch,
				this.maxPitch
			);
			const hitPitchLimit =
				(nextPitch >= this.maxPitch && this.pitchVelocity > 0) ||
				(nextPitch <= this.minPitch && this.pitchVelocity < 0);
			this.pitch = nextPitch;
			this.pitchVelocity *= Math.exp(
				-this.momentumDecay * (1 + (1 - pitchScale) * PITCH_SOFT_LIMIT_EXTRA_DECAY) * dt
			);
			if (hitPitchLimit) this.pitchVelocity = 0;
		}

		const stillActive =
			Math.abs(this.yawVelocity) > MIN_MOMENTUM_VELOCITY ||
			(this.enablePitch && Math.abs(this.pitchVelocity) > MIN_MOMENTUM_VELOCITY);
		if (!stillActive) this.cancelMomentum();
		return { changed: true, active: stillActive };
	}

	cancelMomentum(): void {
		this.yawVelocity = 0;
		this.pitchVelocity = 0;
	}

	private getPitchMomentumScale(velocity: number): number {
		if (!this.enablePitch || Math.abs(velocity) <= MIN_MOMENTUM_VELOCITY) return 0;
		const distanceToLimit = velocity > 0 ? this.maxPitch - this.pitch : this.pitch - this.minPitch;
		if (distanceToLimit >= PITCH_SOFT_LIMIT_ZONE) return 1;
		return smoothstep01(distanceToLimit / PITCH_SOFT_LIMIT_ZONE);
	}

	beginPinch(distance: number): boolean {
		if (!this.enableZoom || distance <= 0) return false;
		this.cancelMomentum();
		this.pinching = true;
		this.dragging = false;
		this.pinchLastDistance = distance;
		return true;
	}

	movePinch(distance: number): boolean {
		if (!this.pinching || !this.enableZoom || this.pinchLastDistance <= 0 || distance <= 0) {
			return false;
		}
		const zoomFactor = this.pinchLastDistance / distance;
		this.radius = clamp(this.radius * zoomFactor, this.minRadius, this.maxRadius);
		this.pinchLastDistance = distance;
		return true;
	}

	endPinch(activeTouchCount: number): void {
		if (activeTouchCount >= 2) return;
		this.pinching = false;
		this.pinchLastDistance = 0;
		this.dragging = false;
	}

	/** Trackpad scrolling supplies its own momentum. Do not seed pointer inertia. */
	orbitByScroll(dx: number, dy: number, height: number): boolean {
		if (!Number.isFinite(dx + dy) || (dx === 0 && dy === 0)) return false;
		this.cancelMomentum();
		const scale: number = (2 * Math.PI * this.rotateSpeed) / Math.max(1, height);
		this.yaw += dx * scale;
		if (this.enablePitch) this.pitch = clamp(this.pitch - dy * scale, this.minPitch, this.maxPitch);
		return true;
	}

	panByScroll(dx: number, dy: number, height: number): boolean {
		if (!Number.isFinite(dx + dy) || (dx === 0 && dy === 0)) return false;
		this.cancelMomentum();
		// Renderer uses Z-up, a 45-degree vertical FOV, and an orbit about target.
		const scale: number = (2 * this.radius * Math.tan(Math.PI / 8)) / Math.max(1, height);
		const sinYaw: number = Math.sin(this.yaw);
		const cosYaw: number = Math.cos(this.yaw);
		const sinPitch: number = Math.sin(this.pitch);
		this.target[0] += (-sinYaw * dx + sinPitch * cosYaw * dy) * scale;
		this.target[1] += (cosYaw * dx + sinPitch * sinYaw * dy) * scale;
		this.target[2] -= Math.cos(this.pitch) * dy * scale;
		return true;
	}

	zoomByWheel(deltaY: number, deltaMode = 0, isPinch = false): boolean {
		if (!this.enableZoom) return false;
		// Normalize wheel units to pixels so mice that report lines/pages match pixel devices.
		let delta = deltaY;
		if (deltaMode === 1) delta *= WHEEL_LINE_HEIGHT_PX;
		else if (deltaMode === 2) delta *= WHEEL_PAGE_HEIGHT_PX;
		// Trackpad pinch (ctrl+wheel) sends tiny deltas; boost it to native pinch-zoom feel.
		const speed = isPinch ? this.wheelZoomSpeed * PINCH_ZOOM_GAIN : this.wheelZoomSpeed;
		const factor = Math.exp(delta * speed);
		this.radius = clamp(this.radius * factor, this.minRadius, this.maxRadius);
		return true;
	}
}
